import { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  InputBase,
  Menu,
  MenuItem,
  Skeleton,
} from "@mui/material";
import { handleErrorMessage } from "helpers/methods";
import strings from "global/constants/StringConstants";
import { SignRequest } from "models/interfaces";
import EmptyState from "global/components/EmptyState/EmptyState";
import templatesStyles from "../Templates.styles";
import templatesTokens from "../Templates.tokens";
import { SearchGlyph } from "../components/TemplatesIcons";
import {
  LibraryCategory,
  LibrarySort,
  LibraryTemplate,
  LibraryTemplateFilter,
} from "./templateLibrary.types";
import {
  fetchLibraryCategories,
  fetchLibraryTemplateCount,
  fetchLibraryTemplates,
  LIBRARY_PAGE_SIZE,
} from "./TemplateLibraryService";
import { ALL_CATEGORY, LIBRARY_SORTS } from "./templateLibraryHelpers";
import { ChevronDownGlyph } from "./TemplateLibraryIcons";
import templateLibraryStyles from "./TemplateLibrary.styles";
import LibraryTemplateCard from "./components/LibraryTemplateCard";
import TemplateLibraryDetail, {
  LibraryActionPermissions,
} from "./TemplateLibraryDetail";

const styles = templateLibraryStyles;
const { toolbar } = templatesTokens;

interface TemplateLibraryProps {
  /** Library template whose detail page is open (from the URL), if any. */
  detailId: string | null;
  onOpenDetail: (id: string) => void;
  onCloseDetail: () => void;
  onGoToMyTemplates: () => void;
  /** Starts the existing "use template" Create Request flow. */
  onUseTemplate: (template: SignRequest) => void;
  /** A copy landed in My Templates. */
  onImported: () => void;
  permissions: LibraryActionPermissions;
}

/**
 * The "Template library" tab: search, category chips and the grid of
 * published templates. Search and category are applied by the server; the
 * next page loads when the end of the grid scrolls into view. Opening a card
 * swaps in its detail page while the list stays mounted underneath, so the
 * search and chip survive the round trip.
 */
const TemplateLibrary = (props: TemplateLibraryProps) => {
  const [templates, setTemplates] = useState<LibraryTemplate[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  /** Which filter the shown results belong to (see `isPending`). */
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [categories, setCategories] = useState<LibraryCategory[]>([]);
  const [category, setCategory] = useState(ALL_CATEGORY);
  const [query, setQuery] = useState("");
  const [searchText, setSearchText] = useState("");
  const [sort, setSort] = useState<LibrarySort>(LIBRARY_SORTS[0].value);
  const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const filter: LibraryTemplateFilter = {
    ...(category !== ALL_CATEGORY ? { category } : {}),
    ...(searchText ? { searchText } : {}),
    sort,
  };
  const filterKey = JSON.stringify([category, searchText, sort, reloadKey]);
  /**
   * True from the moment the filter changes, not only once the fetch effect
   * has run: in between, `templates` still holds the previous filter's
   * results, and an empty "no matches" list must not be read as an empty
   * library (that flashed the wrong empty state and remounted the search box).
   */
  const isPending = isLoading || loadedKey !== filterKey;
  const sortLabel =
    LIBRARY_SORTS.find((option) => option.value === sort)?.label ??
    LIBRARY_SORTS[0].label;

  // Debounce typing before it becomes part of the server filter.
  useEffect(() => {
    const timer = setTimeout(
      () => setSearchText(query.trim()),
      strings.SEARCH_TIME_OUT,
    );
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    fetchLibraryCategories()
      .then((list) => setCategories(Array.isArray(list) ? list : []))
      .catch(() => setCategories([]));
  }, [reloadKey]);

  // First page and the count, whenever the filter changes.
  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    const requestKey = filterKey;
    abortRef.current = controller;
    setIsLoading(true);
    setLoadFailed(false);
    Promise.all([
      fetchLibraryTemplates(1, filter, controller.signal),
      fetchLibraryTemplateCount(filter, controller.signal),
    ])
      .then(([list, count]) => {
        if (controller.signal.aborted) return;
        const items = Array.isArray(list) ? list : [];
        setTemplates(items);
        setTotal(Number(count) || 0);
        setPage(1);
        setHasMore(items.length === LIBRARY_PAGE_SIZE);
        setLoadedKey(requestKey);
      })
      .catch((error: any) => {
        if (controller.signal.aborted || error?.name === "AbortError") return;
        setTemplates([]);
        setTotal(0);
        setHasMore(false);
        setLoadFailed(true);
        setLoadedKey(requestKey);
        handleErrorMessage(error);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
    // `filter` is rebuilt every render; these are its real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, searchText, sort, reloadKey]);

  const loadMore = async () => {
    if (isPending || isLoadingMore || !hasMore) return;
    const signal = abortRef.current?.signal;
    setIsLoadingMore(true);
    try {
      const next = page + 1;
      const list = await fetchLibraryTemplates(next, filter, signal);
      if (signal?.aborted) return;
      const items = Array.isArray(list) ? list : [];
      setTemplates((current) => [...current, ...items]);
      setPage(next);
      setHasMore(items.length === LIBRARY_PAGE_SIZE);
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        // Stop paging: the observer below is rebuilt every render and would
        // fire again at once, retrying (and toasting) in a loop. Changing the
        // search, chip or sort starts over from page 1.
        setHasMore(false);
        handleErrorMessage(error);
      }
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Next page when the end of the grid scrolls into view.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || props.detailId) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  });

  // Publishing requires a category, so the category counts add up to "All".
  const chips = [
    {
      label: ALL_CATEGORY,
      count: categories.reduce((sum, c) => sum + (c.count || 0), 0),
    },
    ...categories.map((c) => ({ label: c.category, count: c.count })),
  ];

  const isFiltered = Boolean(searchText) || category !== ALL_CATEGORY;
  /**
   * Nothing published at all — not a filter that matched nothing. Like My
   * Templates' fresh state, the search and chips come off with it: there is
   * nothing to search.
   */
  const isLibraryEmpty =
    !isPending && !loadFailed && templates.length === 0 && !isFiltered;

  /** Back to the full list — clears the debounce too, so it is immediate. */
  const clearFilters = () => {
    setQuery("");
    setSearchText("");
    setCategory(ALL_CATEGORY);
  };

  const renderNoMatches = () => {
    const term = searchText;
    const inCategory = category !== ALL_CATEGORY;
    let body: string;
    if (term && inCategory) {
      body = `Nothing in "${category}" matches "${term}". Check the spelling or try another name, such as "NDA" or "Lease".`;
    } else if (term) {
      body = `Nothing in the template library matches "${term}". Check the spelling or try another name, such as "NDA" or "Lease".`;
    } else {
      body = `There are no templates in "${category}" yet. Show all templates to browse the rest of the library.`;
    }
    return (
      <EmptyState
        framed
        icon="search"
        title={
          term
            ? "No templates match your search"
            : "No templates in this category"
        }
        body={body}
        primaryAction={{
          label:
            term && inCategory
              ? "Clear search & filters"
              : term
                ? "Clear search"
                : "Show all templates",
          onClick: clearFilters,
        }}
      />
    );
  };

  const renderGrid = () => {
    if (isPending) {
      return (
        <Box sx={styles.grid} aria-busy="true">
          {Array.from({ length: 8 }).map((_, i) => (
            <Box
              key={i}
              sx={[styles.card, { cursor: "default" }]}
              aria-hidden="true"
            >
              <Skeleton
                variant="rounded"
                height={150}
                sx={{ borderRadius: "10px", mb: "14px" }}
              />
              <Skeleton
                variant="rounded"
                width={72}
                height={18}
                sx={{ borderRadius: "999px", mb: "10px" }}
              />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="95%" />
              <Skeleton variant="text" width="60%" />
            </Box>
          ))}
        </Box>
      );
    }
    if (loadFailed) {
      return (
        <EmptyState
          framed
          icon="template"
          title="Couldn't load the template library"
          body="Check your connection and try again."
          primaryAction={{
            label: "Try again",
            onClick: () => setReloadKey((k) => k + 1),
          }}
        />
      );
    }
    if (isLibraryEmpty) {
      return (
        <EmptyState
          framed
          icon="template"
          title="No library templates yet"
          body="Ready-made templates appear here once they are published. Until then, you can build your own in My templates."
          secondaryAction={{
            label: "Go to My templates",
            onClick: props.onGoToMyTemplates,
          }}
        />
      );
    }
    if (templates.length === 0) return renderNoMatches();
    return (
      <>
        <Box sx={styles.grid}>
          {templates.map((template) => (
            <LibraryTemplateCard
              key={template.id}
              template={template}
              onOpen={(t) => props.onOpenDetail(t.id)}
            />
          ))}
        </Box>
        <Box ref={sentinelRef} sx={styles.loadMore}>
          {isLoadingMore && (
            <CircularProgress size={18} aria-label="Loading more templates" />
          )}
        </Box>
      </>
    );
  };

  return (
    <>
      {props.detailId && (
        <TemplateLibraryDetail
          templateId={props.detailId}
          onClose={props.onCloseDetail}
          onGoToMyTemplates={props.onGoToMyTemplates}
          onUseTemplate={props.onUseTemplate}
          onImported={props.onImported}
          permissions={props.permissions}
        />
      )}

      <Box sx={{ display: props.detailId ? "none" : "block" }}>
        {!isLibraryEmpty && (
          <Box sx={styles.toolbar}>
            <Box sx={{ flex: { xs: "1 1 100%", sm: 1 }, minWidth: 0 }}>
              <Box sx={templatesStyles.searchField}>
                <SearchGlyph
                  sx={{
                    width: toolbar.searchIconSize,
                    height: toolbar.searchIconSize,
                    color: toolbar.searchIconColor,
                    flexShrink: 0,
                  }}
                />
                <InputBase
                  id="template_library_search_box"
                  placeholder="Try “NDA”, “Lease”, “Offer letter”"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  inputProps={{ "aria-label": "Search the template library" }}
                  sx={templatesStyles.searchInput}
                />
              </Box>
            </Box>
            <Box sx={styles.sortGroup}>
              <Box
                component="span"
                id="template_library_sort_label"
                sx={styles.sortLabel}
              >
                Sort
              </Box>
              <Box
                component="button"
                type="button"
                id="template_library_sort"
                aria-haspopup="listbox"
                aria-expanded={Boolean(sortAnchor)}
                aria-labelledby="template_library_sort_label template_library_sort"
                onClick={(event: React.MouseEvent<HTMLElement>) =>
                  setSortAnchor(event.currentTarget)
                }
                sx={styles.sortButton}
              >
                {sortLabel}
                <ChevronDownGlyph sx={styles.sortChevron} />
              </Box>
              <Menu
                anchorEl={sortAnchor}
                open={Boolean(sortAnchor)}
                onClose={() => setSortAnchor(null)}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                MenuListProps={{
                  role: "listbox",
                  "aria-label": "Sort templates",
                }}
              >
                {LIBRARY_SORTS.map((option) => (
                  <MenuItem
                    key={option.value}
                    role="option"
                    selected={option.value === sort}
                    aria-selected={option.value === sort}
                    onClick={() => {
                      setSortAnchor(null);
                      setSort(option.value);
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
        )}

        {!loadFailed && !isLibraryEmpty && (
          <Box sx={styles.chips} role="group" aria-label="Categories">
            {categories.length === 0 &&
              isPending &&
              [64, 76, 80, 96].map((width) => (
                <Skeleton
                  key={width}
                  variant="rounded"
                  width={width}
                  height={36}
                  sx={{ borderRadius: "999px" }}
                />
              ))}
            {categories.length > 0 &&
              chips.map((c) => {
                const active = c.label === category;
                return (
                  <Box
                    key={c.label}
                    component="button"
                    type="button"
                    aria-pressed={active}
                    onClick={() => setCategory(c.label)}
                    sx={styles.chip(active)}
                  >
                    {c.label}
                    <Box component="span" sx={styles.chipCount(active)}>
                      {c.count}
                    </Box>
                  </Box>
                );
              })}
            {!isPending && (
              <Box component="span" role="status" sx={styles.resultCount}>
                {total} {total === 1 ? "template" : "templates"}
              </Box>
            )}
          </Box>
        )}

        {renderGrid()}
      </Box>
    </>
  );
};

export default TemplateLibrary;
